import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, downloadPdf } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { T, bi } from "@/lib/i18n";
import { computeCompletion } from "@/lib/completion";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, FileText, Trash2, Download, Pencil, Copy, CalendarClock } from "lucide-react";
import { upcomingDeadlines } from "@/lib/deadlines";

export default function Dashboard() {
  const { user, lang } = useAuth();
  const t = T[lang];
  const navigate = useNavigate();
  const [cvs, setCvs] = useState(null);

  useEffect(() => {
    if (user === false) { navigate("/auth"); return; }
    if (!user) return;
    api.get("/cvs").then((r) => setCvs(r.data)).catch(() => setCvs([]));
  }, [user]);

  const createNew = async () => {
    const { data } = await api.post("/cvs", { title: t.newCv, language: lang, personal: {}, categories: {} });
    navigate(`/builder/${data.id}`);
  };

  const remove = async (id) => {
    if (!window.confirm(t.confirmDelete)) return;
    await api.delete(`/cvs/${id}`);
    setCvs((prev) => prev.filter((c) => c.id !== id));
    toast.success(t.delete);
  };

  const dl = async (cv) => {
    try {
      await downloadPdf(cv.id, cv.language || lang, `${(cv.personal?.fullName || cv.title || "cv").replace(/\s+/g, "_")}.pdf`);
    } catch { toast.error("PDF error"); }
  };

  const duplicate = async (id) => {
    try { const { data } = await api.post(`/cvs/${id}/duplicate`); setCvs((c) => [data, ...c]); toast.success(t.duplicated); }
    catch { toast.error("Error"); }
  };
  const upcoming = upcomingDeadlines(cvs || [], lang, 30);

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        {upcoming.length > 0 && (
          <div data-testid="deadline-alert" className="mb-6 rounded-xl border border-amber-300 bg-amber-50 p-4">
            <div className="flex items-center gap-2 font-bold text-[#1a2e4a] text-sm"><CalendarClock className="h-4 w-4 text-[#c0392b]" /> {t.upcomingTitle}</div>
            <ul className="mt-2 space-y-1">
              {upcoming.slice(0, 5).map((x) => (
                <li key={`${x.cvId}-${x.id}`} data-testid={`deadline-alert-item-${x.id}`} className="text-[13px] text-[#334155] flex flex-wrap gap-x-2">
                  <span className={`font-mono font-bold ${x.days <= 7 ? "text-[#c0392b]" : "text-amber-700"}`}>{x.date}</span>
                  <span>{x.title}</span>
                  <span className="text-[#64748b]">· {x.days} {t.daysLeft} · {x.cvTitle}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-display font-bold text-3xl text-[#1a2e4a]">{t.myCvs}</h1>
          <Button data-testid="create-cv-btn" onClick={createNew} className="bg-[#c0392b] hover:bg-[#a93226] text-white h-11">
            <Plus className="h-4 w-4 mr-2" /> {t.newCv}
          </Button>
        </div>

        {cvs === null ? (
          <div className="text-[#64748b]">...</div>
        ) : cvs.length === 0 ? (
          <div data-testid="empty-cvs" className="bg-white rounded-xl border border-dashed border-[#cbd5e1] p-12 text-center">
            <FileText className="h-10 w-10 text-[#94a3b8] mx-auto mb-4" />
            <p className="text-[#334155]">{t.noCvs}</p>
            <Button onClick={createNew} className="mt-5 bg-[#1a2e4a] hover:bg-[#254066] text-white">
              <Plus className="h-4 w-4 mr-2" /> {t.newCv}
            </Button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {cvs.map((cv) => (
              <div key={cv.id} data-testid={`cv-card-${cv.id}`} className="bg-white rounded-xl border border-[#e2e8f0] p-5 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-display font-bold text-lg text-[#1a2e4a] truncate">
                      {cv.personal?.fullName || cv.title}
                    </div>
                    <div className="text-xs text-[#64748b] mt-0.5">
                      {bi(cv.personal?.targetProgram, lang) || cv.title}
                    </div>
                    <div className="text-[11px] text-[#94a3b8] mt-2">
                      {t.updated}: {cv.updated_at ? new Date(cv.updated_at).toLocaleString() : "-"}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <span className="text-[10px] uppercase font-bold text-white bg-[#1a2e4a] rounded px-2 py-0.5">
                      {(cv.language || "tr").toUpperCase()}
                    </span>
                    {(() => { const { pct, missing } = computeCompletion(cv, lang); return (
                      <span data-testid={`cv-completion-${cv.id}`} className={`text-[10px] font-semibold rounded-full px-2 py-0.5 border ${missing.length === 0 ? "bg-green-50 text-green-700 border-green-200" : "bg-[#fadbd8]/60 text-[#c0392b] border-[#c0392b]/30"}`}>
                        {t.completion} {pct}%
                      </span>
                    ); })()}
                  </div>
                </div>
                <div className="flex gap-2 mt-5">
                  <Button data-testid={`open-cv-${cv.id}`} onClick={() => navigate(`/builder/${cv.id}`)} className="bg-[#1a2e4a] hover:bg-[#254066] text-white h-9 flex-1">
                    <Pencil className="h-4 w-4 mr-1.5" /> {t.open}
                  </Button>
                  <Button data-testid={`download-cv-${cv.id}`} variant="outline" onClick={() => dl(cv)} className="h-9 border-[#1a2e4a] text-[#1a2e4a]">
                    <Download className="h-4 w-4" />
                  </Button>
                  <Button data-testid={`duplicate-cv-${cv.id}`} variant="outline" title={t.duplicate} onClick={() => duplicate(cv.id)} className="h-9 border-[#1a2e4a] text-[#1a2e4a]">
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button data-testid={`delete-cv-${cv.id}`} variant="outline" onClick={() => remove(cv.id)} className="h-9 border-[#c0392b] text-[#c0392b] hover:bg-[#fadbd8]">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
